import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, DatabaseZap, Download, FileUp, FolderOpen, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OVERWRITE_WARNING = 'Import All already has been done, do you wish to overwrite all active files?';

const ENTITY_LABELS = {
  accounts: 'Chart of Accounts',
  employees: 'Employees',
  customers: 'Customers',
  vendors: 'Vendors',
  gl_transactions: 'G/L Transactions',
  payroll_history: 'Payroll History',
};

const STATUS_STYLE = {
  ready: { label: 'Ready', cls: 'bg-emerald-100 text-emerald-700' },
  has_exceptions: { label: 'Rows rejected', cls: 'bg-amber-100 text-amber-800' },
  blocked: { label: 'Does not reconcile', cls: 'bg-red-100 text-red-700' },
  needs_mapping: { label: 'Needs mapping', cls: 'bg-red-100 text-red-700' },
  unassigned: { label: 'Choose type', cls: 'bg-red-100 text-red-700' },
  conflict: { label: 'Duplicate type', cls: 'bg-red-100 text-red-700' },
  needs_client: { label: 'Choose client', cls: 'bg-red-100 text-red-700' },
  ignored: { label: 'Not imported', cls: 'bg-gray-100 text-gray-600' },
};

const SPREADSHEET_EXT = /\.(csv|xlsx|xls)$/i;
const INDEX_EXT = /\.(cdx|idx|ntx|mdx|fpt|dbt|bak|tmp)$/i; // index/memo files sit beside the dBase tables; the tables hold the data
const SOURCES = {
  agdata: { label: 'AgData Blue Skies (dBase files)', accept: '.dbf', ok: (n) => /\.dbf$/i.test(n), noun: 'dBase (.dbf) tables' },
  spreadsheet: { label: 'Another accounting program (CSV / Excel)', accept: '.csv,.xlsx,.xls', ok: (n) => SPREADSHEET_EXT.test(n), noun: 'CSV or Excel (.csv, .xlsx, .xls) files' },
};

const fmt = (n) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

async function api(path, options = {}) {
  const res = await fetch(`${API_URL}/api/migration${path}`, { credentials: 'include', ...options });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

const ImportAll = () => {
  const { companies: clients, selectedCompany, addClient } = useCompany();
  const [entities, setEntities] = useState({});
  const [companyId, setCompanyId] = useState(selectedCompany?.id || 'default');
  const [newClient, setNewClient] = useState(null);   // null = closed, string = name being typed
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [mappings, setMappings] = useState({});
  const [preview, setPreview] = useState(null);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [legacyClient, setLegacyClient] = useState('');
  const [source, setSource] = useState('agdata');
  const [sourcePath, setSourcePath] = useState('');
  const [folderName, setFolderName] = useState('');
  const [expanded, setExpanded] = useState({});
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const src = SOURCES[source];

  const done = !!status?.done;
  const importLocked = done && !overwriteMode;

  const loadStatus = useCallback(async (cid) => {
    const r = await api(`/import-all/status?company_id=${encodeURIComponent(cid)}`);
    if (r.ok) setStatus(r.data);
    else toast.error(r.data.detail || 'Could not load import status');
  }, []);

  // follow the company chosen at the top of the page (it can arrive after this screen first renders)
  useEffect(() => {
    if (selectedCompany?.id) setCompanyId(selectedCompany.id);
  }, [selectedCompany?.id]);

  useEffect(() => {
    (async () => {
      const e = await api('/entities');
      if (e.ok) setEntities(e.data);
    })();
  }, []);

  useEffect(() => {
    setFiles([]); setPreview(null); setAssignments({}); setMappings({}); setOverwriteMode(false); setAccepted(false); setLegacyClient('');
    setSourcePath(''); setFolderName('');
    loadStatus(companyId);
    (async () => {  // the old client number and location remembered from an earlier import
      try {
        const res = await fetch(`${API_URL}/api/company-profile/${encodeURIComponent(companyId)}`, { credentials: 'include' });
        if (!res.ok) return;
        const p = await res.json();
        if (p.legacy_client_no) setLegacyClient(String(p.legacy_client_no));
        if (p.source_path) setSourcePath(p.source_path);
      } catch (e) { /* optional */ }
    })();
  }, [companyId, loadStatus]);

  const buildForm = useCallback((extra = {}, a = assignments, m = mappings, list = files, lc = legacyClient) => {
    const fd = new FormData();
    fd.append('company_id', companyId);
    list.forEach((f) => fd.append('files', f, f.name));
    fd.append('assignments', JSON.stringify(a));
    fd.append('mappings', JSON.stringify(m));
    if (source === 'agdata' && lc) fd.append('agdata_client', String(lc).trim());
    if (sourcePath.trim()) fd.append('source_path', sourcePath.trim());
    Object.entries(extra).forEach(([k, v]) => fd.append(k, String(v)));
    return fd;
  }, [assignments, mappings, files, companyId, legacyClient, source, sourcePath]);

  const runPreview = useCallback(async (a, m, list, overwrite, lc = legacyClient) => {
    if (!list.length) { setPreview(null); return; }
    setBusy(true);
    const r = await api('/import-all/preview', { method: 'POST', body: buildForm({ overwrite }, a, m, list, lc) });
    setBusy(false);
    if (!r.ok) { toast.error(typeof r.data.detail === 'string' ? r.data.detail : 'Could not check the files'); return; }
    if (r.data.already_done) { setPreview(null); await loadStatus(companyId); return; }
    setPreview(r.data);
    setAccepted(false);
    // keep the operator's manual choices, but show what was detected/suggested
    setAssignments((prev) => {
      const next = { ...prev };
      r.data.files.forEach((f) => { if (f.entity && !f.locked && !next[f.filename]) next[f.filename] = f.entity; });
      return next;
    });
  }, [buildForm, companyId, loadStatus, legacyClient]);

  const takeFiles = (picked, fromFolder = false) => {
    const bad = picked.filter((f) => !src.ok(f.name) && !(source === 'agdata' && INDEX_EXT.test(f.name)));
    if (bad.length && !fromFolder) toast.error(`Only ${src.noun} are imported: ${bad.map((f) => f.name).join(', ')}`);
    const ok = picked.filter((f) => src.ok(f.name));
    if (fromFolder && !ok.length) toast.error(`No ${src.noun} found in that folder`);
    setFiles(ok); setAssignments({}); setMappings({}); setPreview(null);
    runPreview({}, {}, ok, overwriteMode, legacyClient);
  };

  const onPickFiles = (e) => {
    setFolderName('');
    takeFiles(Array.from(e.target.files || []));
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickFolder = (e) => {
    const picked = Array.from(e.target.files || []);
    const first = picked[0]?.webkitRelativePath || '';
    setFolderName(first.includes('/') ? first.split('/')[0] : '');
    takeFiles(picked, true);
    if (folderRef.current) folderRef.current.value = '';
  };

  const switchSource = (next) => {
    if (next === source) return;
    setSource(next);
    setFiles([]); setPreview(null); setAssignments({}); setMappings({}); setFolderName('');
  };

  const downloadTemplate = (entity) => {
    const fields = entities[entity]?.fields || [];
    const blob = new Blob([`${fields.join(',')}\n`], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${entity}-template.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const chooseLegacyClient = (no) => {
    setLegacyClient(no);
    runPreview(assignments, mappings, files, overwriteMode, no);
  };

  const applyTypedClient = () => { if (files.length) runPreview(assignments, mappings, files, overwriteMode, legacyClient); };

  const changeType = (filename, entity) => {
    const a = { ...assignments, [filename]: entity };
    const m = { ...mappings }; delete m[filename]; // a different type needs a fresh mapping
    setAssignments(a); setMappings(m);
    runPreview(a, m, files, overwriteMode);
  };

  const changeMapping = (filename, current, field, source) => {
    const m = { ...mappings, [filename]: { ...current, [field]: source || undefined } };
    setMappings(m);
    runPreview(assignments, m, files, overwriteMode);
  };

  const createClient = async () => {
    try {
      const c = await addClient(newClient);
      toast.success(`Client "${c.name}" added. It is now in the company list at the top of the page.`);
      window.dispatchEvent(new Event('companiesChanged'));
      setCompanyId(c.id);
      setNewClient(null);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const startOverwrite = () => {
    setConfirmOverwrite(false);
    setOverwriteMode(true);
    setPreview(null);
    if (files.length) runPreview(assignments, mappings, files, true);
    toast.info('Overwrite mode: choose the files to re-import.');
  };

  const doImport = async () => {
    setBusy(true);
    const r = await api('/import-all', {
      method: 'POST',
      body: buildForm({ confirm: true, overwrite: overwriteMode, accept_exceptions: accepted }),
    });
    setBusy(false);
    if (r.ok) {
      const total = Object.values(r.data.entities).reduce((s, e) => s + e.loaded_documents, 0);
      toast.success(`Import All complete: ${fmt(total)} records loaded${r.data.replaced_records ? `, ${fmt(r.data.replaced_records)} replaced` : ''}.`);
      setFiles([]); setPreview(null); setOverwriteMode(false); setAssignments({}); setMappings({});
      loadStatus(companyId);
    } else if (r.status === 409 && r.data.detail?.code === 'IMPORT_ALL_DONE') {
      await loadStatus(companyId);
      setConfirmOverwrite(true);
    } else if (r.status === 422) {
      setPreview({ ...r.data.detail, already_done: false });
      toast.error(r.data.detail.message || 'Nothing was imported');
    } else {
      toast.error(typeof r.data.detail === 'string' ? r.data.detail : 'Import All failed');
    }
  };

  const undoImport = async () => {
    setConfirmUndo(false);
    setBusy(true);
    const r = await api(`/import-all/${status.run.run_id}/rollback`, { method: 'POST' });
    setBusy(false);
    if (r.ok) { toast.success(`Import All undone: ${fmt(r.data.deleted)} records removed.`); setOverwriteMode(false); loadStatus(companyId); }
    else toast.error(typeof r.data.detail === 'string' ? r.data.detail : 'Could not undo');
  };

  const needsAcceptance = !!preview?.needs_acceptance;
  const canImport = !!preview?.can_import && !busy && !importLocked && (!needsAcceptance || accepted);
  const clientName = useMemo(() => clients.find((c) => c.id === companyId)?.name || companyId, [clients, companyId]);

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Import All Existing Data</h1>
        <p className="text-gray-500 mt-1">
          {source === 'agdata'
            ? 'Bring a client\'s whole AgData Blue Skies Accounting data set across in one step: company settings, chart of accounts, enterprises, operations, equipment, job codes, vendors and employees first, then opening balances, G/L transactions, paychecks and timecards.'
            : 'Moving from another accounting program? Export your chart of accounts, customers, vendors, employees and transactions from it as CSV or Excel files, then bring them in here in one step.'}
        </p>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-700" htmlFor="import-client">Client</label>
          <select
            id="import-client"
            data-testid="import-all-client"
            className="border rounded-md px-3 py-2 text-sm bg-white min-w-[16rem]"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {newClient === null ? (
            <Button variant="outline" size="sm" onClick={() => setNewClient('')} data-testid="new-client-btn">+ New client</Button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                className="border rounded-md px-3 py-2 text-sm bg-white w-64"
                placeholder="Client name, e.g. Progressive Farming Co"
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && newClient.trim().length > 1) createClient(); }}
                data-testid="new-client-name"
              />
              <Button size="sm" disabled={newClient.trim().length < 2} onClick={createClient} data-testid="new-client-add">Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setNewClient(null)}>Cancel</Button>
            </div>
          )}
          <p className="w-full text-xs text-gray-500">
            The imported data is saved under this client. To see it afterwards, choose the same client in the company list at the top of the page.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3" data-testid="import-source">
          <span className="text-sm font-medium text-gray-700">Coming from</span>
          {Object.entries(SOURCES).map(([k, v]) => (
            <Button key={k} size="sm" variant={source === k ? 'default' : 'outline'} disabled={busy || importLocked} onClick={() => switchSource(k)} data-testid={`source-${k}`}>
              {v.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      {done && (
        <Card className="border-emerald-300 bg-emerald-50/60" data-testid="import-all-done-banner">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-900">
                  Import All was completed for {clientName} on {new Date(status.run.created_at).toLocaleString()}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(status.run.entities).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="bg-white">
                      {v.label || ENTITY_LABELS[k] || k}: {fmt(v.loaded_documents)}
                    </Badge>
                  ))}
                </div>
                {(status.run.legacy_client_no || status.run.source_path) && (
                  <p className="text-sm text-emerald-800 mt-2" data-testid="import-source-info">
                    {status.run.legacy_client_no ? <>Old BSA client number <b>{status.run.legacy_client_no}</b>. </> : null}
                    {status.run.source_path ? <>Source: <span className="font-mono">{status.run.source_path}</span>. </> : null}
                    {status.run.source_files?.length ? `${status.run.source_files.length} source file${status.run.source_files.length > 1 ? 's' : ''} recorded.` : null}
                  </p>
                )}
                <p className="text-sm text-emerald-800 mt-2">
                  Import All is switched off for this client so it cannot be run twice by accident.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pl-9">
              <Button variant="outline" disabled={busy || overwriteMode} onClick={() => setConfirmOverwrite(true)} data-testid="overwrite-btn">
                <DatabaseZap className="w-4 h-4 mr-2" />Overwrite existing import…
              </Button>
              <Button variant="ghost" className="text-red-700" disabled={busy} onClick={() => setConfirmUndo(true)} data-testid="undo-import-btn">
                <RotateCcw className="w-4 h-4 mr-2" />Undo Import All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {overwriteMode && (
        <Card className="border-amber-300 bg-amber-50" data-testid="overwrite-mode-banner">
          <CardContent className="p-4 flex items-center gap-3 text-amber-900 text-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            Overwrite mode: the previous import for {clientName} will be replaced only after the new files have been
            fully loaded. Records entered by hand since then are kept.
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => { setOverwriteMode(false); setPreview(null); setFiles([]); }}>Cancel</Button>
          </CardContent>
        </Card>
      )}

      <Card className={importLocked ? 'opacity-60' : ''}>
        <CardHeader>
          <CardTitle className="text-base">{source === 'agdata' ? '1. Identify the AgData client and choose its files' : '1. Choose the files exported from your old program'}</CardTitle>
          <CardDescription>
            {source === 'agdata'
              ? 'Enter the client\'s old BSA client number, then select the client\'s AgData folder (or all its .dbf files at once, for example GLAC1000.DBF, GLTR1000.DBF, PRMS1000.DBF). AgData tables are recognised by name.'
              : 'Select all the files at once. Each file is matched to chart of accounts, customers, vendors, employees, G/L transactions or payroll history from its name and column headings; you can change any match below.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {source === 'agdata' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm text-gray-700 flex flex-col gap-1">
                <span className="font-medium">Old BSA client number</span>
                <input
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  placeholder="e.g. 1000 (the number in GLAC1000.DBF)"
                  inputMode="numeric"
                  value={legacyClient}
                  disabled={importLocked}
                  onChange={(e) => setLegacyClient(e.target.value.replace(/\D/g, ''))}
                  onBlur={applyTypedClient}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyTypedClient(); }}
                  data-testid="legacy-client-number"
                />
                <span className="text-xs text-gray-500">Checked against the files, so the right client is imported even when the folder holds several.</span>
              </label>
              <label className="text-sm text-gray-700 flex flex-col gap-1">
                <span className="font-medium">Where the database lives (for the record)</span>
                <input
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  placeholder="e.g. C:\AGDATA\CLIENT1000"
                  value={sourcePath}
                  disabled={importLocked}
                  onChange={(e) => setSourcePath(e.target.value)}
                  data-testid="source-path"
                />
                <span className="text-xs text-gray-500">Saved with the import so the source can always be traced.</span>
              </label>
            </div>
          )}
          {source === 'spreadsheet' && (
            <label className="text-sm text-gray-700 flex flex-col gap-1 sm:w-1/2">
              <span className="font-medium">Which program did the files come from? (for the record)</span>
              <input className="border rounded-md px-3 py-2 text-sm bg-white" placeholder="e.g. QuickBooks export, Sage, Excel workbook"
                value={sourcePath} disabled={importLocked} onChange={(e) => setSourcePath(e.target.value)} data-testid="source-path" />
            </label>
          )}
        </CardContent>
        <CardContent className="space-y-4 pt-0">
          <div className="flex flex-wrap items-center gap-3">
            <input ref={inputRef} type="file" multiple accept={src.accept} className="hidden" onChange={onPickFiles} data-testid="import-all-files" />
            <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={onPickFolder} data-testid="import-all-folder" />
            {source === 'agdata' && (
              <Button variant="outline" disabled={importLocked || busy} onClick={() => folderRef.current?.click()} data-testid="pick-folder-btn">
                <FolderOpen className="w-4 h-4 mr-2" />Select client folder
              </Button>
            )}
            <Button variant="outline" disabled={importLocked || busy} onClick={() => inputRef.current?.click()}>
              <FileUp className="w-4 h-4 mr-2" />{source === 'agdata' ? 'Select .dbf files' : 'Select files'}
            </Button>
            {busy && <Loader2 className="w-4 h-4 animate-spin text-sky-600" />}
            {files.length > 0 && (
              <span className="text-sm text-gray-500">
                {files.length} file{files.length > 1 ? 's' : ''} selected{folderName ? ` from folder "${folderName}"` : ''}
              </span>
            )}
          </div>

          {source === 'spreadsheet' && Object.keys(entities).length > 0 && (
            <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2" data-testid="template-downloads">
              <span>Need a layout to follow? Download a blank CSV with the columns we recognise:</span>
              {Object.keys(ENTITY_LABELS).filter((k) => entities[k]).map((k) => (
                <Button key={k} variant="ghost" size="sm" className="h-7 px-2" onClick={() => downloadTemplate(k)}>
                  <Download className="w-3 h-3 mr-1" />{ENTITY_LABELS[k]}
                </Button>
              ))}
            </div>
          )}

          {preview?.agdata_clients?.length > 1 && (
            <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-sm flex flex-wrap items-center gap-3" data-testid="legacy-client-picker">
              <span className="text-sky-900">These files belong to several AgData clients. Import which one?</span>
              <select
                className="border rounded-md px-2 py-1 bg-white"
                value={legacyClient}
                onChange={(e) => chooseLegacyClient(e.target.value)}
                data-testid="legacy-client-select"
              >
                <option value="" disabled>Choose client…</option>
                {preview.agdata_clients.map((c) => (
                  <option key={c.client_no} value={c.client_no}>{c.client_no}{c.name ? ` — ${c.name}` : ''}</option>
                ))}
              </select>
            </div>
          )}

          {preview?.mode === 'agdata' && preview.client_name && (
            <p className="text-sm text-gray-600">AgData client <b>{preview.client_no}</b> — {preview.client_name}</p>
          )}

          {preview?.files?.map((f) => (
            <FileRow
              key={f.unit || f.filename}
              f={f}
              entities={entities}
              open={!!expanded[f.filename]}
              toggle={() => setExpanded((p) => ({ ...p, [f.filename]: !p[f.filename] }))}
              onType={(e) => changeType(f.filename, e)}
              onMap={(field, src) => changeMapping(f.filename, f.mapping || {}, field, src)}
            />
          ))}

          {preview?.issues?.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1" data-testid="import-all-issues">
              {preview.issues.map((i) => (
                <p key={i.code}><AlertTriangle className="w-4 h-4 inline mr-1" />{i.message} e.g. {i.examples.join(', ')}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={importLocked ? 'opacity-60' : ''}>
        <CardHeader>
          <CardTitle className="text-base">2. Import</CardTitle>
          <CardDescription>Nothing is written until every file reconciles. Setup information is loaded before transactions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {needsAcceptance && !importLocked && (
            <label className="flex items-start gap-2 text-sm text-amber-900">
              <input type="checkbox" className="mt-1" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="accept-exceptions" />
              I understand the rejected rows and issues above will be left out of the import (they are saved in a log to fix and re-run).
            </label>
          )}
          <Button
            size="lg"
            disabled={!canImport}
            title={importLocked ? 'Import All has already been done for this client' : undefined}
            onClick={doImport}
            data-testid="import-all-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DatabaseZap className="w-4 h-4 mr-2" />}
            {overwriteMode ? 'Overwrite with Import All' : source === 'agdata' ? 'Import All AgData Existing Data' : 'Import All Existing Data'}
          </Button>
          {importLocked && <p className="text-sm text-gray-500">Disabled: Import All was already done for this client.</p>}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOverwrite} onOpenChange={setConfirmOverwrite}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import All already done</AlertDialogTitle>
            <AlertDialogDescription data-testid="overwrite-warning">{OVERWRITE_WARNING}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, keep it</AlertDialogCancel>
            <AlertDialogAction onClick={startOverwrite} data-testid="confirm-overwrite">Yes, overwrite</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmUndo} onOpenChange={setConfirmUndo}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo Import All?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every record the import created for {clientName}. Records entered by hand are kept. Import All will be available again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={undoImport} data-testid="confirm-undo">Yes, undo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const FileRow = ({ f, entities, open, toggle, onType, onMap }) => {
  const st = STATUS_STYLE[f.status] || { label: f.status, cls: 'bg-gray-100 text-gray-700' };
  const r = f.report;
  const fields = f.entity && entities[f.entity] ? entities[f.entity].fields : [];
  const required = f.entity && entities[f.entity] ? entities[f.entity].required : [];
  return (
    <div className="border rounded-lg" data-testid={`file-row-${f.unit || f.filename}`}>
      <div className="p-3 flex flex-wrap items-center gap-3">
        <button className="text-gray-500" onClick={toggle} aria-label="Details">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <span className="font-mono text-sm font-medium">{f.filename}</span>
        <span className="text-xs text-gray-500">{fmt(f.row_count)} rows</span>
        {f.locked ? (
          <span className="text-sm font-medium text-gray-800">{f.label}</span>
        ) : f.status === 'ignored' || f.status === 'needs_client' ? null : (
          <select
            className="border rounded-md px-2 py-1 text-sm bg-white"
            value={f.entity || ''}
            onChange={(e) => onType(e.target.value)}
            data-testid={`type-${f.filename}`}
          >
            <option value="" disabled>Choose type…</option>
            {Object.entries(ENTITY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        )}
        {f.phase && <Badge variant="outline">{f.phase === 'setup' ? 'Setup' : 'Transactions'}</Badge>}
        <Badge className={`${st.cls} ml-auto`}>{st.label}</Badge>
      </div>

      {f.issue && (
        <p className={`px-4 pb-3 text-sm ${f.status === 'ignored' ? 'text-gray-500' : 'text-red-700'}`}>
          {f.status !== 'ignored' && <XCircle className="w-4 h-4 inline mr-1" />}{f.issue}
        </p>
      )}
      {f.missing && <p className="px-4 pb-3 text-sm text-red-700">Map these required fields: {f.missing.join(', ')}</p>}

      {r && (
        <div className="px-4 pb-3 text-sm text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
          <span>Loads: <b>{fmt(r.loaded_documents)}</b>{f.entity === 'gl_transactions' || f.unit === 'GLTR' ? ' entries' : ''}</span>
          <span>Rejected rows: <b className={r.exception_rows ? 'text-amber-700' : ''}>{fmt(r.exception_rows)}</b></span>
          <span>Unaccounted rows: <b className={r.unaccounted_rows ? 'text-red-700' : ''}>{fmt(r.unaccounted_rows)}</b></span>
          {f.entity === 'gl_transactions' && <span>Debits {fmt(r.loaded_total_debits)} = Credits {fmt(r.loaded_total_credits)}</span>}
        </div>
      )}

      {open && (
        <div className="border-t p-4 space-y-4 bg-slate-50/60">
          {!f.locked && fields.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Column mapping (legacy column → Blue Skies field)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {fields.map((field) => (
                  <label key={field} className="text-xs text-gray-600 flex flex-col gap-1">
                    <span>{field}{required.includes(field) ? ' *' : ''}</span>
                    <select
                      className="border rounded-md px-2 py-1 text-sm bg-white"
                      value={(f.mapping || {})[field] || ''}
                      onChange={(e) => onMap(field, e.target.value)}
                    >
                      <option value="">—</option>
                      {f.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          )}
          {f.exceptions?.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-1">
                Rejected rows{f.exception_count > f.exceptions.length ? ` (first ${f.exceptions.length} of ${fmt(f.exception_count)})` : ''}
              </p>
              <div className="max-h-48 overflow-auto rounded border bg-white text-xs">
                {f.exceptions.map((e, i) => (
                  <div key={i} className="px-2 py-1 border-b last:border-0">
                    <span className="text-gray-500">row {e.row}</span> <span className="font-mono text-amber-700">{e.code}</span> {e.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportAll;
